import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMapContext } from '../context/MapContext';

const EditTools = ({ showStatus }) => {
  const { t } = useTranslation();
  const { 
    selectedFeature, 
    isEditing, 
    startEditing, 
    setIsEditing,
    finishEditing,
    simplifyGeometry,
    deleteFeature,
    simplifyTolerance,
    setSimplifyTolerance
  } = useMapContext();
  
  // Handle edit vertices button
  const handleEditVertices = () => {
    if (!selectedFeature) {
      showStatus(t('editTools.messages.selectToEdit'), 'error');
      return;
    }
    
    // Start editing mode
    startEditing();
    
    // We're only using the Finish Editing button from the application
    // rather than the Leaflet Draw toolbar
    
    showStatus(t('editTools.messages.editingActive'), 'info');
  };
  
  // Handle finish editing button
  const handleFinishEditing = () => {
    setIsEditing(false);
    showStatus(t('editTools.messages.editsSaved'), 'success');
  };
  
  // Handle geometry edit completion - wrapped in useCallback to avoid recreation on every render
  const handleEditComplete = useCallback((geometry) => {
    // Save the edited geometry
    finishEditing(geometry);
    showStatus(t('editTools.messages.geometryApplied'), 'success');
  }, [finishEditing, showStatus, t]);
  
  // Handle simplify geometry button
  const handleSimplify = () => {
    if (!canSimplify) return;
    
    const result = simplifyGeometry();
    if (result.success) {
      showStatus(
        t('editTools.messages.simplifySuccess', { 
          before: result.before, 
          after: result.after,
          percent: Math.round((1 - (result.after / result.before)) * 100)
        }),
        'success'
      );
    } else if (result.message === 'No reduction in vertices. Try a larger tolerance value.') {
      // Show a specific message when simplification didn't reduce vertices
      showStatus(t('editTools.messages.noReduction'), 'warning');
    } else {
      // Generic error message for other failures
      showStatus(t('editTools.messages.simplifyError'), 'error');
    }
  };
  
  // Handle tolerance change
  const handleToleranceChange = (e) => {
    const value = parseFloat(e.target.value);
    console.log('Changing simplify tolerance to:', value);
    setSimplifyTolerance(value);
  };
  
  // Handle delete feature button
  const handleDelete = () => {
    if (!selectedFeature) {
      showStatus(t('editTools.messages.selectToDelete'), 'error');
      return;
    }
    
    // Get the DUN name and code (using DUN_CODE or fallback to KODDUN for compatibility)
    const name = selectedFeature.feature.properties.DUN_NAME || '';
    const code = selectedFeature.feature.properties.DUN_CODE || selectedFeature.feature.properties.KODDUN || '';
    
    // Confirm deletion
    if (window.confirm(t('editTools.messages.deleteConfirm', { name, code }))) {
      const success = deleteFeature();
      
      if (success) {
        showStatus(t('editTools.messages.featureDeleted'), 'info');
      } else {
        showStatus(t('editTools.messages.deleteError'), 'error');
      }
    }
  };
  
  // Expose handleEditComplete for MapEditor to use
  React.useEffect(() => {
    // Make handleEditComplete available on window object temporarily
    // so MapEditor can access it
    window.handleEditComplete = handleEditComplete;
    
    return () => {
      // Clean up when component unmounts
      delete window.handleEditComplete;
    };
  }, [handleEditComplete]); // Add handleEditComplete to the dependency array
  
  // Determine which buttons should be enabled
  const canEdit = selectedFeature && !isEditing;
  const canFinishEditing = isEditing;
  const canSimplify = selectedFeature && !isEditing;
  const canDelete = selectedFeature && !isEditing;
  
  return (
    <div>
      <div className="btn-group">
        <button 
          className={`btn ${canEdit ? 'btn-primary' : 'btn-secondary'}`}
          onClick={handleEditVertices}
          disabled={!canEdit}
          title={!selectedFeature ? t('editTools.tooltips.selectDunFirst') : (isEditing ? t('editTools.tooltips.finishCurrentEdit') : t('editTools.tooltips.editBoundary'))}
        >
          <i className="fas fa-edit"></i> {t('editTools.editBoundary')}
        </button>
        <button 
          className={`btn ${canFinishEditing ? 'btn-success' : 'btn-secondary'}`}
          onClick={handleFinishEditing}
          disabled={!canFinishEditing}
          title={!isEditing ? t('editTools.tooltips.noActiveEditing') : t('editTools.tooltips.saveCurrentEdits')}
        >
          <i className="fas fa-check"></i> {t('editTools.finishEditing')}
        </button>
      </div>
      
      <div className="btn-group">
        <button 
          className={`btn ${canSimplify ? 'btn-secondary' : 'btn-light'}`}
          onClick={handleSimplify}
          disabled={!canSimplify}
          title={!selectedFeature ? t('editTools.tooltips.selectDunFirst') : (isEditing ? t('editTools.tooltips.finishEditing') : t('editTools.tooltips.reducePoints'))}
        >
          <i className="fas fa-compress-arrows-alt"></i> {t('editTools.simplifyGeometry')}
        </button>
        <button 
          className={`btn ${canDelete ? 'btn-danger' : 'btn-light'}`}
          onClick={handleDelete}
          disabled={!canDelete}
          title={!selectedFeature ? t('editTools.tooltips.selectDunFirst') : (isEditing ? t('editTools.tooltips.finishEditing') : t('editTools.tooltips.deleteDun'))}
        >
          <i className="fas fa-trash"></i> {t('editTools.deleteFeature')}
        </button>
      </div>
      
      <div className="control-group">
        <label className="control-label">{t('editTools.simplificationTolerance')}</label>
        <div className="tolerance-control">
          <input 
            type="range" 
            min="0.0001" 
            max="0.01" 
            step="0.0001" 
            value={simplifyTolerance}
            onChange={handleToleranceChange}
            disabled={isEditing || !selectedFeature}
            title={!selectedFeature ? t('editTools.tooltips.selectDunFirst') : (isEditing ? t('editTools.tooltips.finishEditing') : t('editTools.tooltips.adjustSimplification'))}
          />
          <button 
            className="btn btn-sm btn-outline-info ml-2"
            onClick={() => handleSimplify()}
            disabled={!canSimplify}
          >
            {t('editTools.apply')}
          </button>
        </div>
        <div className="tolerance-value">
          {t('editTools.toleranceValue')}: {simplifyTolerance.toFixed(6)}
        </div>
      </div>
    </div>
  );
};

export default EditTools;
